import classNames from 'classnames';
import { createAnamnesis, useAnamneses } from 'data/useAnamnesis';
import { createExamination, useExaminations } from 'data/useExaminations';
import { createNote, useNotes } from 'data/useNotes';
import { createIndication, useIndications } from 'data/useIndication';
import { Patient } from 'data/usePatients';
import { createPrescription, usePrescriptions } from 'data/usePrescriptions';
import { ReactComponent as Plus } from 'icons/plus.svg';
import { ReactComponent as CheckMark } from 'icons/checkmark2.svg';
import {
  ButtonHTMLAttributes,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GlobalErrorContext } from 'contexts/GlobalError';
import { useAppointmentContext } from 'contexts/Appointment';
import { PdfReceiptModal } from '../PharmaciesIntegrations/modals/PdfReceiptModal';
import { Doctor } from '../../types/doctor';
import DigitalReceiptModal from '../PharmaciesIntegrations/modals/DigitalReceiptModal';
import { Pharmacy, usePharmacies } from '../../data/usePharmacies';
import useConfig from '../../hooks/useConfig';
import { TherapyState } from '../../types/therapyState';

type PatientActionsProps = {
  patient: Patient;
  appointmentId: number;
  doctor: Doctor;
  pharmacyId: number | null;
  handleRefetchAppointments: () => void;
  wasPrescriptionSent: boolean;
  wasPaymentLinkSent: boolean;
  therapyState: TherapyState;
};

export default function PatientActions({
  patient,
  appointmentId,
  doctor,
  pharmacyId,
  handleRefetchAppointments,
  wasPrescriptionSent,
  wasPaymentLinkSent,
  therapyState,
}: PatientActionsProps) {
  const { setError } = useContext(GlobalErrorContext);
  const { mutate: mutateAnamneses } = useAnamneses(patient.id, appointmentId);
  const { mutate: mutateExaminations } = useExaminations(patient.id, appointmentId);
  const { mutate: mutateNotes } = useNotes(patient.id, appointmentId);
  const { mutate: mutatePrescriptions, results: prescriptions } = usePrescriptions(
    patient.id,
    appointmentId,
  );
  const { mutate: mutateIndications } = useIndications(patient.id, appointmentId);
  const { sectionExistsInDOM, setActive, setSelectedSection, setShowEmptyDiagnosis, state } =
    useAppointmentContext();
  const [showPdfReceiptModal, setShowPdfReceiptModal] = useState(false);
  const [showDigitalReceiptModal, setShowDigitalReceiptModal] = useState(false);
  const [pharmacy, setPharmacy] = useState<Pharmacy>();
  const { data: pharmacies } = usePharmacies();
  const { data: config, isLoading: isLoadingConfig } = useConfig();

  useEffect(() => {
    if (!pharmacies) {
      return;
    }
    setPharmacy(pharmacies.find((p) => p.id === pharmacyId));
  }, [pharmacyId, pharmacies]);

  const showPdfPrescriptionButton = useMemo(() => {
    return config?.prescriptionsConfig.allowsPdfGeneration;
  }, [isLoadingConfig]);

  const showDigitalPrescriptionButton = useMemo(() => {
    return pharmacy?.integration_type === 'propharma-integration';
  }, [pharmacy]);

  const isTherapyStarted = useMemo(() => {
    return therapyState !== TherapyState.Started;
  }, [therapyState]);

  return (
    <div className="border-solid border-[#f0f0f0] border bg-[#fafafa] p-3 rounded-md mb-10">
      <PlusButton
        onClick={async () => {
          if (sectionExistsInDOM('anamnesis')) return;
          try {
            await createAnamnesis({ id: patient.id, appointmentId });
            await mutateAnamneses();
            setSelectedSection('anamnesis');
            setActive('anamnesis');
          } catch (e: any) {
            setError(e);
            setSelectedSection('');
          }
        }}
        active={state.anamnesisActive}
        multiple={false}
      >
        Anamnese
      </PlusButton>
      <PlusButton
        onClick={async () => {
          if (sectionExistsInDOM('examination')) return;
          try {
            await createExamination({ id: patient.id, appointmentId });
            await mutateExaminations();
            setSelectedSection('examination');
            setActive('examination');
          } catch (e: any) {
            setError(e);
            setSelectedSection('');
          }
        }}
        active={state.examinationActive}
        multiple={false}
      >
        Körperliche Untersuchung
      </PlusButton>
      <PlusButton
        onClick={() => {
          if (sectionExistsInDOM('diagnosis')) return;
          setShowEmptyDiagnosis(true);
          setSelectedSection('diagnosis');
          setActive('diagnosis');
        }}
        active={state.diagnosisActive}
        multiple={false}
      >
        Diagnose
      </PlusButton>
      <PlusButton
        onClick={async () => {
          if (sectionExistsInDOM('indication')) return;
          try {
            await createIndication({ patient: patient.id, appointmentId });
            await mutateIndications();
            setSelectedSection('indication');
            setActive('indication');
          } catch (e: any) {
            setError(e);
            setSelectedSection('');
          }
        }}
        active={state.indicationActive}
        multiple={false}
      >
        Indikation
      </PlusButton>
      <PlusButton
        onClick={async () => {
          try {
            await createPrescription({
              patient: patient.id,
              appointmentId,
            });
            await mutatePrescriptions();
            setSelectedSection('prescription');
            setActive('prescription');
          } catch (e: any) {
            setError(e);
            setSelectedSection('');
          }
        }}
        active={false}
        multiple={prescriptions.length > 0}
        disabled={wasPrescriptionSent}
      >
        Verschreibung
      </PlusButton>
      <PlusButton
        onClick={async () => {
          if (sectionExistsInDOM('note')) return;
          try {
            await createNote({ patient: patient.id, appointmentId });
            await mutateNotes();
            setSelectedSection('note');
            setActive('note');
          } catch (e: any) {
            setError(e);
            setSelectedSection('');
          }
        }}
        active={state.noteActive}
        multiple={false}
      >
        Notiz
      </PlusButton>
      {showPdfPrescriptionButton && (
        <PlusButton active={false} multiple={false} onClick={() => setShowPdfReceiptModal(true)}>
          PDF Rezept
        </PlusButton>
      )}
      {showDigitalPrescriptionButton && (
        <PlusButton
          active={false}
          multiple={false}
          onClick={() => setShowDigitalReceiptModal(true)}
          disabled={isTherapyStarted}
          helpText="Setzen Sie den Therapiestatus auf Starten/fortfuhren"
        >
          An Apotheke senden
        </PlusButton>
      )}
      <PdfReceiptModal
        open={showPdfReceiptModal}
        patient={patient}
        doctor={doctor}
        pharmacyId={pharmacyId}
        appointmentId={appointmentId}
        onClose={() => setShowPdfReceiptModal(false)}
      />
      <DigitalReceiptModal
        wasPaymentLinkSent={wasPaymentLinkSent}
        open={showDigitalReceiptModal}
        patient={patient}
        doctor={doctor}
        pharmacyId={pharmacyId}
        appointmentId={appointmentId}
        onClose={() => setShowDigitalReceiptModal(false)}
        handleRefetchAppointments={handleRefetchAppointments}
      />
    </div>
  );
}

type PlusButtonProps = PropsWithChildren<{}> &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    active: boolean;
    multiple?: boolean;
    helpText?: string;
  };

function PlusButton({
  children,
  className,
  active,
  multiple,
  helpText,
  disabled,
  ...rest
}: PlusButtonProps) {
  return (
    <button
      {...rest}
      className={classNames(
        className,
        'inline-flex group items-center m-1 last:mr-0 px-4 py-0.5 text-swopa-secondary-light-blue border border-solid border-swopa-secondary-light-blue rounded-md hover:text-white hover:bg-swopa-secondary-light-blue disabled:text-swopa-grey-4 disabled:border-current disabled:hover:bg-transparent',
        { 'bg-[#E7F0FA]': active || multiple },
        { 'hover:bg-[#E7F0FA]': active || multiple },
        { 'hover:text-swopa-secondary-light-blue': active || multiple },
        { 'pointer-events-none': active },
        { 'cursor-help': disabled },
      )}
      title={disabled ? helpText : ''}
      disabled={disabled}
    >
      {active || multiple ? (
        <>
          <CheckMark className="stroke-current mr-1 group-hover:hidden h-[9px]" />
          <Plus
            className={classNames('stroke-current mr-2 fill-current h-[9px]', {
              'hidden group-hover:inline-block': multiple || active,
            })}
          />
        </>
      ) : (
        <Plus className="stroke-current fill-current mr-1" />
      )}
      {children}
    </button>
  );
}
